import React, { useState } from 'react';
import { Avatar, Dropdown, Typography, Space, Tag, Tooltip } from 'antd';
import { LogoutOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useLogout, useGetIdentity } from '@refinedev/core';
import { User } from '@src/types';
import { useAvatarRender } from '@src/hooks/useAvatarRender';
import './ProfileMenu.css';

const { Text } = Typography;

interface ProfileMenuProps {
  collapsed?: boolean;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  collapsed = false,
}) => {
  const { mutate: logout } = useLogout();
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
  } = useGetIdentity<User>();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const avatarRender = useAvatarRender({
    size: 40,
    fontSize: '16px',
    useConsistentColors: true,
    showFallbackIcon: true,
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Handle logout error silently
    }
  };

  const profileMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const userInfo = {
    name:
      user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`.trim()
        : user?.first_name || user?.last_name || user?.name || 'User',
    email: user?.email || 'user@example.com',
    avatar: user?.avatar || null,
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
  };

  const userForAvatar = {
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    avatar: user?.avatar,
  };

  const ProfileDropdown = ({ children }: { children: React.ReactNode }) => (
    <Dropdown
      menu={{ items: profileMenuItems }}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      placement="topRight"
    >
      {children}
    </Dropdown>
  );

  if (collapsed) {
    return (
      <ProfileDropdown>
        <div className="profile-menu-collapsed">
          {userLoading ? (
            <Avatar
              size={40}
              icon={<LoadingOutlined />}
              className="profile-avatar"
            />
          ) : (
            <div className="profile-avatar-container">
              {avatarRender(userForAvatar)}
            </div>
          )}
        </div>
      </ProfileDropdown>
    );
  }

  return (
    <ProfileDropdown>
      <div className="profile-menu-expanded">
        <div className="profile-avatar-section">
          {userLoading ? (
            <Avatar
              size={44}
              icon={<LoadingOutlined />}
              className="profile-avatar-large"
            />
          ) : (
            <div className="profile-avatar-container-large">
              {avatarRender(userForAvatar)}
            </div>
          )}
        </div>
        
        <div className="profile-info-section">
          <div className="profile-text-container">
            <div className="profile-name">
              {userLoading ? 'Loading...' : userInfo.name}
            </div>
            <div className="profile-email">
              {userLoading
                ? 'Loading...'
                : userError
                  ? 'Error loading user'
                  : userInfo.email}
            </div>
            {!userLoading && !userError && user?.type && (
              <Tag className="profile-user-type">
                {user.type.replace(/_/g, ' ').toLowerCase()}
              </Tag>
            )}
          </div>
        </div>
        
        <div className="profile-dropdown-arrow">
          <DownOutlined className={`arrow-icon ${dropdownOpen ? 'rotated' : ''}`} />
        </div>
      </div>
    </ProfileDropdown>
  );
};
